import type {
  Bot,
  Lifecycle,
  ServiceAction,
  ServiceConversationActivity,
  ServiceTicket,
  ServiceWorkspaceSnapshot,
  ThreadMessage,
} from "@rakazo/contracts";

export type {
  HqContext,
  Lifecycle,
  ServiceChannel,
  ServiceConversationActivity,
  ServiceEvent,
  ServiceThread,
  ServiceTicket,
} from "@rakazo/contracts";

export type ServicePreviewData = {
  bots: Bot[];
  tickets: ServiceTicket[];
  messages: Record<string, ThreadMessage[]>;
  conversationActivities: ServiceConversationActivity[];
};

export function selectTickets(tickets: readonly ServiceTicket[], mode: Lifecycle, owner?: string) {
  return tickets
    .filter((ticket) => ticket.lifecycle === mode && (!owner || ticket.ownerBotId === owner))
    .toSorted((a, b) => a.rank - b.rank);
}

/** Lifecycle and supervisor decisions never remove or silently reassign the bot owner. */
export function changeLifecycle(ticket: ServiceTicket, lifecycle: Lifecycle): ServiceTicket {
  return {
    ...ticket,
    lifecycle,
    decision: undefined,
    humanHandling: false,
    step: lifecycle === "closed" ? "Resolved" : "Reviewing reopened issue",
    nextAction: lifecycle === "closed" ? "Completed" : "Review the reopened issue",
    due: lifecycle === "closed" ? "Completed" : "Rep resumes follow-up",
    resolution:
      lifecycle === "closed"
        ? ticket.resolution || "Issue reviewed and resolved."
        : ticket.resolution,
    closedAt: lifecycle === "closed" ? "Just now · preview" : undefined,
  };
}

export function authorizeSupplierReview(ticket: ServiceTicket): ServiceTicket {
  if (ticket.decision !== "supplier-review" || ticket.lifecycle !== "active") return ticket;
  return {
    ...ticket,
    decision: undefined,
    step: "Requesting supplier review",
    nextAction: "Request the supplier review",
    due: "Authorization received",
    events: [
      ...ticket.events,
      {
        id: `review-${ticket.id}`,
        channel: "internal",
        author: "You",
        time: "Just now",
        body: "Supplier review authorized. The bot retains ownership. No deposit waiver promised.",
      },
    ],
  };
}

export function emailReplyThread(ticket: ServiceTicket, id: string) {
  return ticket.threads.find((thread) => thread.id === id && thread.channel === "email") ?? null;
}

export type ServiceView = "work" | "team" | "tickets";
export function serviceView(value: string | null): ServiceView | undefined {
  return value === "work" || value === "team" || value === "tickets" ? value : undefined;
}
export function resolveServiceTicket(tickets: readonly ServiceTicket[], id: string | null) {
  return id
    ? tickets.find((ticket) => ticket.id === id || ticket.aliases?.includes(id))
    : undefined;
}
export function conversationCardIndex(
  tickets: readonly ServiceTicket[],
  activities: readonly ServiceConversationActivity[],
) {
  const byTicket = new Map(
    tickets.flatMap((ticket) =>
      [ticket.id, ...(ticket.aliases ?? [])].map((id) => [id, ticket] as const),
    ),
  );
  const byMessage = new Map<
    string,
    {
      activity: ServiceConversationActivity;
      ticket: ServiceTicket;
      thread: ServiceTicket["threads"][number];
    }[]
  >();
  for (const activity of activities) {
    const ticket = byTicket.get(activity.ticketId);
    const thread = ticket?.threads.find((item) => item.id === activity.threadId);
    if (!ticket || !thread) continue;
    const rows = byMessage.get(activity.messageId) ?? [];
    if (!rows.some((row) => row.activity.id === activity.id))
      rows.push({ activity, ticket, thread });
    byMessage.set(activity.messageId, rows);
  }
  return byMessage;
}

export type CustomerServiceState = {
  snapshot: ServiceWorkspaceSnapshot;
  loading: boolean;
  error: unknown;
  refreshing: boolean;
  pending: boolean;
  refresh: () => unknown;
  act: (action: ServiceAction) => Promise<ServiceWorkspaceSnapshot>;
};
